import { Controller } from "@hotwired/stimulus";

const SVG_NS = "http://www.w3.org/2000/svg";

// Read-only blueprint overlay for the public profile (custom_figures#show).
//
// Takes the figure's main-showcase annotations (passed in as a JSON value)
// and paints:
//   * A flat yellow dot at each pin's (x, y) on the image.
//   * One Z-shaped yellow leader line from the dot to each tagged
//     collaborator badge in the right sidebar. Multi-collaborator pins
//     draw multiple independent leaders all originating at the same dot.
//
// Two-state opacity model — absolute values applied to each element,
// no parent opacity, no filter, no stacking:
//   * 30%   — default / resting state
//   * 100%  — "active" pair: one dot and the lines/badges tied to it
//
// Focus model:
//   * On load, the topmost annotation (smallest y) is auto-active.
//   * Hovering a dot makes its callout active.
//   * Hovering a tagged collaborator badge makes EVERY callout that
//     credits that collaborator active simultaneously (so you can
//     see at a glance which parts of the build a person worked on).
//   * Focus is sticky: it stays put after the pointer leaves the
//     dot or badge, until something else gets hovered.
//
// Markup contract (set on the hero flex row in show.html.erb):
//
//   <div data-controller="showcase-blueprint"
//        data-showcase-blueprint-annotations-value='[{ "id": 1, "x": 0.5, "y": 0.4, "collaboration_ids": [7, 9] }]'>
//     <svg data-showcase-blueprint-target="overlay"></svg>
//     <div data-showcase-blueprint-target="picture">
//       <img data-showcase-blueprint-target="image">
//     </div>
//     <a data-showcase-blueprint-target="badge" data-collaboration-id="7">…</a>
//   </div>
export default class extends Controller {
  static targets = ["image", "overlay", "badge", "picture", "toggleIcon", "toggleLabel"];
  static values  = { annotations: Array };

  connect() {
    this._annotations = this.annotationsValue || [];
    this._boundLayout = this._layout.bind(this);
    // Overlay defaults to OFF — the showcase photo should read
    // cleanly first. Viewers opt in via the "Show Contributions"
    // button. We pick up the initial state from the data attribute
    // the view stamps onto the controller root so SSR and JS agree
    // on what's visible during hydration.
    this._hidden = this.element.dataset.bpHidden === "true";
    // Last thing the user hovered. `null` falls back to the topmost
    // annotation as the default active set on first paint.
    //   { kind: "dot",    id: <annotation_id>     }
    //   { kind: "collab", id: <collaboration_id>  }
    this._lastHover = null;

    window.addEventListener("resize",   this._boundLayout);
    window.addEventListener("scroll",   this._boundLayout, { passive: true });
    document.addEventListener("turbo:load", this._boundLayout);

    if (this.hasImageTarget) {
      this.imageTarget.addEventListener("load", this._boundLayout);
    }
    this.element.addEventListener("showcase-blueprint:image-loaded", this._boundLayout);

    // First pass deferred so the image rect is populated.
    requestAnimationFrame(this._boundLayout);
  }

  disconnect() {
    window.removeEventListener("resize", this._boundLayout);
    window.removeEventListener("scroll", this._boundLayout);
    document.removeEventListener("turbo:load", this._boundLayout);
    this.element.removeEventListener("showcase-blueprint:image-loaded", this._boundLayout);
    this._clearDots();
  }

  // Show/hide button. Hidden state nukes the overlay paths and
  // removes appended dot spans, clears any active focus, and drops
  // bp-tagged on badges so the sidebar visually returns to a
  // non-annotated state. The button label flips between "Show /
  // Hide Contributions" but the x-ray icon stays put for both
  // states — the verb in the label carries the action affordance.
  toggleVisibility(event) {
    if (event) event.preventDefault();
    this._hidden = !this._hidden;
    this.element.dataset.bpHidden = this._hidden ? "true" : "false";

    if (this.hasToggleLabelTarget) {
      this.toggleLabelTarget.textContent = this._hidden ? "Show Contributions" : "Hide Contributions";
    }

    if (this._hidden) {
      this._lastHover = null;
      this._clearDots();
      this.overlayTarget.innerHTML = "";
      this.badgeTargets.forEach(b => {
        delete b.dataset.bpTagged;
        delete b.dataset.bpActive;
      });
    } else {
      this._layout();
    }
  }

  // Annotations that should currently render at 100%. Resolves
  // `_lastHover` (or the topmost-by-y default) into the concrete
  // set of annotations whose dot + lines should light up:
  //   * dot hover    → just that annotation
  //   * collab hover → every annotation that credits that collab
  //   * default      → just the topmost annotation
  _activeAnnotations() {
    if (this._lastHover?.kind === "dot") {
      const ann = this._annotations.find(a => a.id === this._lastHover.id);
      return ann ? [ann] : [];
    }
    if (this._lastHover?.kind === "collab") {
      return this._annotations.filter(a =>
        (a.collaboration_ids || []).includes(this._lastHover.id)
      );
    }
    const top = this._topmost();
    return top ? [top] : [];
  }

  _topmost() {
    const tagged = this._annotations.filter(a => (a.collaboration_ids || []).length > 0);
    if (tagged.length === 0) return null;
    return tagged.reduce((top, a) => (a.y < top.y ? a : top), tagged[0]);
  }

  // Stimulus calls these as badges enter / leave the DOM, so any
  // dynamically-rendered or Turbo-replaced badge picks up the hover
  // handler automatically without us having to re-bind everything
  // on each layout pass.
  badgeTargetConnected(badge) {
    if (badge._bpBound) return;
    badge._bpBound = true;
    badge.addEventListener("mouseenter", () => {
      if (this._hidden) return;
      const id = parseInt(badge.dataset.collaborationId, 10);
      if (Number.isNaN(id)) return;
      // Only hovering a tagged badge does anything — hovering a
      // collaborator with no callouts shouldn't blank the overlay.
      const hits = this._annotations.some(a =>
        (a.collaboration_ids || []).includes(id)
      );
      if (!hits) return;
      this._lastHover = { kind: "collab", id };
      this._refreshActive();
    });
  }

  // Stamps `data-bp-tagged` onto every badge that's referenced by at
  // least one annotation so the CSS can scope the active-state
  // highlight to just those badges. Idempotent.
  _markTaggedBadges() {
    const tagged = new Set(this._annotations.flatMap(a => a.collaboration_ids || []));
    this.badgeTargets.forEach(badge => {
      const id = parseInt(badge.dataset.collaborationId, 10);
      if (tagged.has(id)) badge.dataset.bpTagged = "true";
      else                delete badge.dataset.bpTagged;
    });
  }

  _clearDots() {
    const root = this.hasPictureTarget ? this.pictureTarget : this.element;
    root.querySelectorAll(".showcase-blueprint-dot, .showcase-blueprint-label").forEach(d => d.remove());
  }

  _layout() {
    if (this._hidden) return;
    if (!this.hasImageTarget || !this.hasOverlayTarget) return;
    if (this._annotations.length === 0) {
      this._clearDots();
      this.overlayTarget.innerHTML = "";
      return;
    }

    const img     = this.imageTarget;
    const overlay = this.overlayTarget;
    if (img.clientWidth === 0 || img.clientHeight === 0) return;

    // SVG viewBox spans the hero row in CSS pixels so paths can reach
    // from a dot in the image area all the way to a sidebar badge.
    const containerRect = this.element.getBoundingClientRect();
    overlay.setAttribute("viewBox", `0 0 ${containerRect.width} ${containerRect.height}`);

    const toLocal = (rect) => ({
      left:   rect.left   - containerRect.left,
      right:  rect.right  - containerRect.left,
      top:    rect.top    - containerRect.top,
      bottom: rect.bottom - containerRect.top,
      width:  rect.width,
      height: rect.height
    });

    const imgRect = img.getBoundingClientRect();
    const imgBox  = toLocal(imgRect);

    // Dot positions are kept in two coordinate spaces:
    //   * image-relative for the absolutely-positioned dot spans
    //     (they're appended inside `.image-container`).
    //   * hero-row-relative for the SVG path endpoints.
    const pictureRect = this.hasPictureTarget
      ? this.pictureTarget.getBoundingClientRect()
      : imgRect;

    // Re-derive everything from scratch on every layout pass so
    // dynamic changes stay in sync without per-element bookkeeping.
    overlay.innerHTML = "";
    this._clearDots();
    this._markTaggedBadges();

    const badgesById = new Map();
    this.badgeTargets.forEach(badge => {
      const id = parseInt(badge.dataset.collaborationId, 10);
      if (!Number.isNaN(id)) badgesById.set(id, badge);
    });

    for (const ann of this._annotations) {
      const ids = ann.collaboration_ids || [];
      const badges = ids
        .map(id => badgesById.get(id))
        .filter(Boolean)
        .map(b => ({ badge: b, rect: toLocal(b.getBoundingClientRect()) }));

      if (badges.length === 0) continue;

      // Hero-row-relative dot coords (used to build SVG paths).
      const dotX = imgBox.left + ann.x * imgBox.width;
      const dotY = imgBox.top  + ann.y * imgBox.height;

      // Picture-container-relative dot coords (used to position the
      // absolutely-placed dot span inside `.image-container`).
      const dotPicX = (imgRect.left - pictureRect.left) + ann.x * imgRect.width;
      const dotPicY = (imgRect.top  - pictureRect.top ) + ann.y * imgRect.height;

      this._appendDot(ann, dotPicX, dotPicY);
      // Its label: where the owner placed it (fractions of the picture,
      // like the pin), else beside the pin.
      if (ann.label) {
        const placed = ann.label_x != null && ann.label_y != null;
        // A label moved away from its pin is tethered to it.
        if (placed) {
          const tether = document.createElementNS(SVG_NS, "path");
          tether.setAttribute("d", `M ${dotX} ${dotY} L ${imgBox.left + ann.label_x * imgBox.width} ${imgBox.top + ann.label_y * imgBox.height}`);
          tether.classList.add("showcase-blueprint-tether");
          tether.dataset.annotationId = String(ann.id);
          overlay.appendChild(tether);
        }
        this._appendLabel(ann,
          placed ? (imgRect.left - pictureRect.left) + ann.label_x * imgRect.width : dotPicX,
          placed ? (imgRect.top - pictureRect.top) + ann.label_y * imgRect.height : dotPicY,
          placed);
      }

      badges.forEach(({ badge, rect }) => {
        const badgeToTheRight = rect.left >= imgBox.right - 1;
        // Anchor flush against the badge's image-facing edge so the
        // butt-capped stroke butts up directly against the badge
        // outline. Vertical midpoint on the badge so multi-line
        // wrapping doesn't shift the landing point.
        const anchor = badgeToTheRight
          ? { x: rect.left,                       y: (rect.top + rect.bottom) / 2 }
          : { x: (rect.left + rect.right) / 2,    y: rect.top                     };

        const collabId = parseInt(badge.dataset.collaborationId, 10);
        overlay.appendChild(
          this._mkPath(ann, collabId, this._leaderPath(dotX, dotY, anchor.x, anchor.y, badgeToTheRight))
        );
      });
    }

    this._refreshActive();
  }

  // Z-leader from the dot to a single badge anchor. Starts at the
  // RIGHT edge of the dot (dotX + DOT_RADIUS) so the line visually
  // connects to the dot rather than piercing through it. Keep
  // DOT_RADIUS in sync with the dot's `width` in application.css
  // (currently 28px ⇒ 14px radius).
  _leaderPath(dotX, dotY, anchorX, anchorY, badgeToTheRight) {
    const TAIL = 36;
    const DOT_RADIUS = 14;
    const startX = dotX + DOT_RADIUS;
    if (badgeToTheRight) {
      return `M ${startX} ${dotY} L ${startX + TAIL} ${dotY} L ${anchorX - TAIL} ${anchorY} L ${anchorX} ${anchorY}`;
    } else {
      return `M ${startX} ${dotY} L ${startX} ${dotY + TAIL} L ${anchorX} ${anchorY - TAIL} L ${anchorX} ${anchorY}`;
    }
  }

  // Single flat yellow stroke per leader. Butt caps so the tips
  // meet the dot edge and badge outline cleanly; round line joins
  // on the Z elbow so the corners stay smooth.
  // Tagged with BOTH `data-annotation-id` AND `data-collaboration-id`
  // so `_refreshActive` can scope activation to either:
  //   * every line owned by an annotation (dot hover / default), or
  //   * just the line(s) pointing AT a given badge (collab hover).
  // The latter is what keeps a joint-credit pin from lighting BOTH
  // of its outgoing lines when you only meant to focus on one of
  // the credited collaborators.
  _mkPath(ann, collabId, d) {
    const p = document.createElementNS(SVG_NS, "path");
    p.setAttribute("d", d);
    p.setAttribute("fill", "none");
    p.setAttribute("stroke", "#facc15"); // yellow-400 — matches dots
    p.setAttribute("stroke-width", "6");
    p.setAttribute("stroke-linecap", "butt");
    p.setAttribute("stroke-linejoin", "round");
    p.classList.add("showcase-blueprint-line");
    p.dataset.annotationId    = String(ann.id);
    p.dataset.collaborationId = String(collabId);
    return p;
  }

  _appendDot(ann, x, y) {
    const dot = document.createElement("span");
    dot.className = "showcase-blueprint-dot absolute z-20";
    dot.style.left = `${x}px`;
    dot.style.top  = `${y}px`;
    dot.dataset.annotationId = String(ann.id);

    // Hovering a dot makes its callout active and STAYS that way
    // after the pointer leaves — until another dot OR badge is
    // hovered. No mouseleave handler on purpose.
    dot.addEventListener("mouseenter", () => {
      this._lastHover = { kind: "dot", id: ann.id };
      this._refreshActive();
    });

    const parent = this.hasPictureTarget ? this.pictureTarget : this.element;
    parent.appendChild(dot);
  }

  // A pin's optional label, shown while its callout is active: centred on
  // where it was placed, or just to the left of its pin (its leader lines
  // run right, to the collaborators).
  _appendLabel(ann, x, y, placed) {
    const label = document.createElement("span");
    label.className = `showcase-blueprint-label${placed ? " is-placed" : ""}`;
    label.textContent = ann.label;
    label.style.left = `${x}px`;
    label.style.top  = `${y}px`;
    label.dataset.annotationId = String(ann.id);
    const parent = this.hasPictureTarget ? this.pictureTarget : this.element;
    parent.appendChild(label);
  }

  // Resolves the current active annotation set and stamps
  // `data-bp-active="true"` on:
  //   * every dot whose annotation is active
  //   * every line tied to one of those annotations
  //   * every badge credited by one of those annotations,
  //     EXCEPT when the user is hovering a specific collaborator
  //     badge — in that case only that one badge highlights, even
  //     if its active annotations also credit other people. That
  //     keeps the "hovering Mechazinger" mental model clean: it
  //     spotlights Mechazinger's contributions, not everyone who
  //     happened to share a callout with him.
  // Clears the attribute from everything else. Single source of
  // truth for the active visual state.
  _refreshActive() {
    const activeAnns   = this._activeAnnotations();
    const activeAnnIds = new Set(activeAnns.map(a => a.id));
    const activeCollabIds = this._lastHover?.kind === "collab"
      ? new Set([this._lastHover.id])
      : new Set(activeAnns.flatMap(a => a.collaboration_ids || []));

    const root = this.hasPictureTarget ? this.pictureTarget : this.element;
    const marks = [ ...root.querySelectorAll(".showcase-blueprint-dot, .showcase-blueprint-label") ];
    if (this.hasOverlayTarget) marks.push(...this.overlayTarget.querySelectorAll(".showcase-blueprint-tether"));
    marks.forEach(dot => {
      const match = activeAnnIds.has(parseInt(dot.dataset.annotationId, 10));
      if (match) dot.dataset.bpActive = "true";
      else       delete dot.dataset.bpActive;
    });

    if (this.hasOverlayTarget) {
      const hoveredCollab = this._lastHover?.kind === "collab" ? this._lastHover.id : null;
      this.overlayTarget.querySelectorAll(".showcase-blueprint-line").forEach(p => {
        const annId = parseInt(p.dataset.annotationId,    10);
        const colId = parseInt(p.dataset.collaborationId, 10);
        // For collab hover, also require the line to terminate at
        // that collaborator's badge — so a multi-credit pin only
        // lights up the line pointing at the hovered person, not
        // every line it owns.
        const match = activeAnnIds.has(annId) &&
          (hoveredCollab == null || colId === hoveredCollab);
        if (match) p.dataset.bpActive = "true";
        else       delete p.dataset.bpActive;
      });
    }

    this.badgeTargets.forEach(badge => {
      const id = parseInt(badge.dataset.collaborationId, 10);
      if (activeCollabIds.has(id)) badge.dataset.bpActive = "true";
      else                          delete badge.dataset.bpActive;
    });
  }
}
